import { publishedAt, modifiedAt, postDateByline } from "@/data/postDates";
import { getPost } from "@/data/posts";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { articleSchema, canonicalLinks, jsonLd, ogImage, ogUrl, breadcrumbSchema, webPageSchema, absoluteUrl } from "@/lib/seo";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PhotoBand } from "@/components/site/PhotoBand";
import siteOfficePlansImg from "@/assets/photos/site-office-plans.webp";

const SLUG = "mining-services-first-90-days";
const TITLE = "Ninety days into a mining services engagement";
const DESCRIPTION =
  "The first 90 days of an Ajility mining services engagement: a one-page plan, a weekly cadence, forecasting and about $1M of annualised overhead removed.";
const CATEGORY = "Case study";
const READ = "4 min read";

export const Route = createFileRoute("/insights/mining-services-first-90-days")({
  head: () => ({
    meta: [
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage(`insights-${SLUG}`, TITLE),
      ogUrl(`/insights/${SLUG}`),
    ],
    links: canonicalLinks(`/insights/${SLUG}`),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Insights", path: "/insights" }, { name: TITLE, path: `/insights/${SLUG}` }])),
      jsonLd(
        webPageSchema({
          path: `/insights/${SLUG}`,
          name: `${TITLE} | Ajility`,
          description: DESCRIPTION,
          dateModified: modifiedAt(SLUG),
          mainEntityId: `${absoluteUrl(`/insights/${SLUG}`)}#article`,
          image: `insights-${SLUG}`,
        }),
      ),
      jsonLd(
        articleSchema({
          title: TITLE,
          description: DESCRIPTION,
          slug: SLUG,
          datePublished: publishedAt(SLUG),
          dateModified: modifiedAt(SLUG),
          section: CATEGORY,
        }),
      ),
    ],
  }),
  component: MiningServicesFirst90Days,
});

function MiningServicesFirst90Days() {
  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative gradient-navy text-frost">
        <div className="grid-lines absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-3xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-frost/70 transition hover:text-frost"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All operating insights and case studies
          </Link>
          <span className="eyebrow on-dark mt-8 block">{CATEGORY}, {READ}</span>
          <h1 className="mt-4 font-serif text-4xl leading-[1.08] text-frost sm:text-5xl">
            {TITLE}
          </h1>
          <p className="mt-3 text-sm text-frost/70">{postDateByline(SLUG)}</p>
          <p className="mt-6 text-lg leading-relaxed text-frost/80">{DESCRIPTION}</p>
          <div className="mt-10 flex items-center gap-4 border-t border-white/10 pt-6 text-sm text-frost/70">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-frost/10 font-serif text-frost">
              A
            </div>
            <div>
              By <Link to="/about/adam-ayoub" className="font-medium text-frost underline decoration-white/30 underline-offset-4 transition hover:decoration-frost">Adam Ayoub</Link>
              <br />
              <span className="text-frost/80">Fractional COO, Ajility</span>
            </div>
          </div>
        </div>
      </section>


      <PhotoBand src={siteOfficePlansImg} alt="A site office desk with plans and schedules spread out, looking onto a light-vehicle yard beyond the window." />

      {/* Article */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <article className="article-body">
          <p>Ninety days into an Ajility engagement with a mining services business:</p>
          <p>A one-page strategic plan the leadership team actually uses. A weekly leadership cadence. P&L and cashflow forecasting where there was none. About $1M of annualised overhead removed, none of it from critical roles.</p>
          <p>No transformation program. No 200-slide deck. Just the operating basics, installed in the order that pays for itself first.</p>
          <p>The pattern repeats in almost every founder-led business at this size: the work is good, the customers are there, and the operating layer never got built because everyone was too busy doing the work. The founder holds the plan in their head, the numbers arrive weeks after the month ends, and every escalation lands on the same desk.</p>
          <p>Ninety days is enough to change that. The diagnostic that starts it is priced on the site.</p>
          <p>
            <Link to="/packages" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">
              See packages and prices
            </Link>
          </p>
          <p>For the full case, read <Link to="/insights/$slug" params={{ slug: "mining-90-days" }} className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">{getPost("mining-90-days")?.title}</Link>.</p>
        </article>
      </section>

      {/* CTA */}
      <section className="bg-midnight-deep">
        <div className="mx-auto max-w-4xl px-5 py-20 text-center text-frost sm:px-8">
          <h2 className="font-serif text-4xl leading-tight sm:text-5xl">
            Want to know where your real bottleneck is?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-frost/75">
            Book a 30-minute fit call, a structured conversation rather than a pitch, about
            where the constraint actually sits in your business.
          </p>
          <BookFitCall
            placement={`insights_article_${SLUG}_footer`}
            buttonClassName="mt-8 inline-flex items-center gap-2 rounded-full bg-frost px-7 py-3.5 text-sm font-medium text-midnight-deep transition hover:bg-white"
            noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
          >
            Book a fit call <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </BookFitCall>
          <p className="mt-8 font-serif italic text-frost/80">
            "Strategy without execution is hallucination."
          </p>
        </div>
      </section>
    </div>
  );
}
